import React from 'react';

import { GroupDropIndicator } from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/group-drop-indicator';
import {
	extractInstruction,
	type Instruction,
} from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/hitbox';
import { MenuList } from '@atlaskit/navigation-system/side-nav-items/menu-list';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import type { GlobalSideNavigationProps } from '../../common/types';
import NavItem from '../../common/ui/nav-item';
import { isL1ItemData, isMoreMenuData } from '../../common/utils/dnd-data';
import { useL1DragState } from '../../common/utils/use-l1-drag-state';
import { useModifyNavItemForAnalytics } from '../../common/utils/use-modify-nav-item-for-analytics';
import { useNavAnalytics } from '../../common/utils/use-nav-analytics';
import { createElementRegistry, ElementRegistryProvider } from '../../controllers/element-registry';
import {
	type ItemsAndShortcutsProviderProps,
	useItemsAndShortcuts,
} from '../../controllers/items-and-shortcuts-provider';
import { type OptionsProviderProps } from '../../controllers/options-provider';
import { useDragAndDropSave } from '../../controllers/use-drag-and-drop-save';
import { useUserMenuDataShapes } from '../../controllers/user-state/user-menu-data-shapes-provider';
import { GLOBAL_SIDE_NAV_TEST_ID } from '../constants';
import { SideNavigationLayout } from '../layout';
import { PostMainItems } from '../post-main-items';

export type SideNavProps = Omit<
	GlobalSideNavigationProps,
	keyof OptionsProviderProps | keyof ItemsAndShortcutsProviderProps | 'peekMenuId'
>;

export function SideNav(props: SideNavProps) {
	const { bottomElem, postItemsElem, postMainElem, topElem } = props;

	const { allItems, shortcuts } = useItemsAndShortcuts();
	const userMenuDataShapes = useUserMenuDataShapes();
	const { fireViewSideNavEvent, fireClickSideNavItemEvent } = useNavAnalytics();
	const { modifyNavItemForAnalytics } = useModifyNavItemForAnalytics();

	// #region Nav item rendering
	const renderNavItem = React.useCallback(
		(id: string, index: number) => {
			const handleClickSideNavItem = () => fireClickSideNavItemEvent(id, index);
			const item = modifyNavItemForAnalytics(id, allItems[id]);

			return <NavItem key={id} id={id} item={item} onClick={handleClickSideNavItem} />;
		},
		[allItems, fireClickSideNavItemEvent, modifyNavItemForAnalytics],
	);
	// #endregion

	// #region Drag and drop
	// Element registry for L1 items
	const [elementRegistry] = React.useState(createElementRegistry);
	const { dragAndDropSave } = useDragAndDropSave(userMenuDataShapes, elementRegistry);
	const [draggingL1Item, setDraggingL1Item] = React.useState(false);
	React.useEffect(() => {
		return monitorForElements({
			canMonitor: ({ source }) => isL1ItemData(source.data),
			onDragStart({ source }) {
				if (!isL1ItemData(source.data)) {
					return;
				}
				setDraggingL1Item(true);
			},
			onDrop({ source, location }) {
				const dragging = source.data;
				setDraggingL1Item(false);

				const [innerMost] = location.current.dropTargets;

				if (!innerMost) {
					return;
				}
				const dropTargetData = innerMost.data;

				const instruction: Instruction | null = extractInstruction(dropTargetData);
				if (!instruction) {
					return;
				}

				if (isL1ItemData(dragging) && isL1ItemData(dropTargetData)) {
					dragAndDropSave({
						type: 'item-reorder',
						draggedMenuItemId: dragging.menuItemId,
						draggedMenuItemLabel: dragging.label,
						droppedOnMenuItemId: dropTargetData.menuItemId,
						instruction,
						trigger: 'pointer',
					});
					return;
				}

				if (isL1ItemData(dragging) && isMoreMenuData(dropTargetData)) {
					dragAndDropSave({
						type: 'item-hide',
						draggedMenuItemId: dragging.menuItemId,
						draggedMenuItemLabel: dragging.label,
						instruction,
						trigger: 'pointer',
					});
					return;
				}
			},
		});
	}, [dragAndDropSave]);

	const { groupDropIndicatorRef } = useL1DragState();
	// #endregion

	// #region Core rendering
	const mainNavItems = userMenuDataShapes.visibleItemMenuIds.map(renderNavItem);

	React.useLayoutEffect(() => {
		fireViewSideNavEvent();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ElementRegistryProvider registry={elementRegistry}>
			<SideNavigationLayout
				testId={props.testId ?? GLOBAL_SIDE_NAV_TEST_ID}
				preMainElem={topElem}
				postMainElem={
					<PostMainItems
						postItemsElem={postItemsElem}
						postMainElem={postMainElem}
						shortcuts={shortcuts}
						draggingL1Item={draggingL1Item}
					/>
				}
				bottomElem={bottomElem}
			>
				<MenuList>
					<GroupDropIndicator ref={groupDropIndicatorRef} isActive={draggingL1Item}>
						{mainNavItems}
					</GroupDropIndicator>
				</MenuList>
			</SideNavigationLayout>
		</ElementRegistryProvider>
	);
	// #endregion
}
